// src/pages/CourseDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import coursesData from '../data/courses.json';

const CourseDetail = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [content, setContent] = useState('');
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCourse = async () => {
      setLoading(true);
      
      try {
        // Find course metadata
        const crs = coursesData.find(c => c.id === id);
        if (!crs) {
          setLoading(false);
          return;
        }

        setCourse(crs);

        // Load markdown content for local courses
        if (crs.source === 'local') {
          try {
            const response = await fetch(`${process.env.PUBLIC_URL}/courses/content/${id}.md`);
            if (response.ok) {
              const text = await response.text();
              setContent(text);
            } else {
              setContent(`# ${crs.title}\n\n${crs.description}\n\n## Course Content\n\nDetailed course content coming soon...`);
            }
          } catch (error) {
            console.error('Error loading course content:', error);
            setContent(`# ${crs.title}\n\n${crs.description}`);
          }
        } else {
          // For external courses, show platform information
          setContent(`# ${crs.title}\n\n${crs.description}\n\n## Course Information\n\nThis course is hosted on **${crs.platform}**. Click the button below to access the course directly on their platform.`);
        }

        // Find related courses (same category)
        const related = coursesData
          .filter(c => c.id !== id && c.category === crs.category)
          .slice(0, 3);
        setRelatedCourses(related);
      } catch (error) {
        console.error('Error loading course:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [id]);

  if (loading) {
    return (
      <div className="full-section">
        <div className="loading">Loading course details...</div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="full-section">
        <div className="not-found">
          <h1>Course not found</h1>
          <Link to="/courses" className="btn-primary">
            Back to Courses
          </Link>
        </div>
      </div>
    );
  }

  const getPlatformIcon = (platform) => {
    if (platform.includes('AWS')) return '☁️';
    if (platform.includes('Coursera')) return '🎓';
    if (platform.includes('Google')) return '🔍';
    if (platform.includes('Linux')) return '🐧';
    if (platform.includes('Educative')) return '📖';
    if (platform.includes('DeepLearning')) return '🧠';
    if (platform.includes('Stanford')) return '🎓';
    return '📚';
  };

  return (
    <section className="full-section course-detail">
      <div className="course-header">
        <Link to="/courses" className="back-link">
          ← Back to Courses
        </Link>
        
        <div className="course-meta-header">
          <div className="course-category-tag">{course.category}</div>
          <div className="course-level">{course.level}</div>
          {course.free && <div className="course-free-badge">🆓 Free Course</div>}
          {course.certificate && <div className="course-cert-badge">📜 Certificate Available</div>}
        </div>

        <h1>{course.title}</h1>
        <p className="course-description">{course.description}</p>
        
        <div className="course-info-grid">
          <div className="info-card">
            <div className="info-icon">{getPlatformIcon(course.platform)}</div>
            <div>
              <h4>Platform</h4>
              <p>{course.platform}</p>
            </div>
          </div>
          
          <div className="info-card">
            <div className="info-icon">⏱️</div>
            <div>
              <h4>Duration</h4>
              <p>{course.duration}</p>
            </div>
          </div>
          
          <div className="info-card">
            <div className="info-icon">📅</div>
            <div>
              <h4>Completed</h4>
              <p>{new Date(course.date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          
          {!course.free && (
            <div className="info-card">
              <div className="info-icon">💰</div>
              <div>
                <h4>Price</h4>
                <p>{course.price}</p>
              </div>
            </div>
          )}
        </div>

        <div className="course-instructor">
          <h4>Instructor{course.instructor.includes(' & ') ? 's' : ''}</h4>
          <p>{course.instructor}</p>
        </div>
      </div>

      <div className="course-content-container">
        <article className="markdown-content">
          <ReactMarkdown>{content}</ReactMarkdown>
        </article>

        <div className="skills-section">
          <h3>Skills You'll Gain</h3>
          <div className="skills-grid">
            {course.skills.map((skill, index) => (
              <div key={index} className="skill-item">
                <span className="skill-check">✓</span>
                <span>{skill}</span>
              </div>
            ))}
          </div>
        </div>

        {course.source === 'local' ? (
          <div className="enrollment-section">
            <h3>Enroll in this Course</h3>
            <p>This is a locally hosted course. Content will be available here.</p>
            <div className="enrollment-actions">
              <button className="btn-primary">Start Learning</button>
              <button className="btn-secondary">Download Syllabus</button>
            </div>
          </div>
        ) : (
          <div className="external-course-section">
            <h3>Access this Course</h3>
            <p>This course is hosted externally on {course.platform}. Click below to access it on their platform.</p>
            <div className="external-actions">
              <a 
                href={course.externalUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Go to {course.platform}
              </a>
              <Link to="/courses" className="btn-secondary">
                Browse More Courses
              </Link>
            </div>
            <div className="platform-note">
              <p><small>You'll be redirected to {course.platform}'s website. Course availability and pricing may vary.</small></p>
            </div>
          </div>
        )}

        {relatedCourses.length > 0 && (
          <div className="related-courses">
            <h3>Related Courses</h3>
            <div className="related-grid">
              {relatedCourses.map(crs => (
                <Link key={crs.id} to={`/courses/${crs.id}`} className="related-card">
                  <div className="related-card-header">
                    <span className="related-category">{crs.category}</span>
                    {crs.free && <span className="related-free">🆓 Free</span>}
                  </div>
                  <h4>{crs.title}</h4>
                  <p>{crs.description.substring(0, 100)}...</p>
                  <div className="related-meta">
                    <span>{crs.platform}</span>
                    <span>•</span>
                    <span>{crs.duration}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CourseDetail;